import { CommunicationLanguage, ContactLanguages, ViewCommunicationItem } from 'briefpoint-client';
import Button from 'components/Button';
import Help from 'components/Help';
import Sparkle from 'components/Sparkle';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Stack } from 'react-bootstrap';
import { Check } from 'react-bootstrap-icons';
import { sanitizeHtmlString } from 'utils';
import { IntLi } from '.';
import { ReactComponent as ArrowOff } from '../../images/arrow-right-off.svg';
import { ReactComponent as Arrow } from '../../images/arrow-right.svg';

export default function InterrogatoryListItem({
  interrogatory,
  isSelected,
  isSimplified,
  isSavingOutside,
  language,
  liRef,
  questionType,
  readonly,
  english,
  spanish,
  onSaveCommunicationItem,
  onSelect,
  onSimplify,
  onTranslate,
  canSpanish,
}: {
  interrogatory: IntLi;
  isSelected: boolean;
  isSimplified: boolean;
  isSavingOutside: boolean;
  language?: ContactLanguages;
  liRef: React.RefObject<HTMLDivElement>;
  questionType: string;
  readonly: boolean;
  english?: ViewCommunicationItem;
  spanish?: ViewCommunicationItem;
  onSaveCommunicationItem: (
    lang: CommunicationLanguage,
    val: string,
    id?: string
  ) => Promise<ViewCommunicationItem | undefined>;
  onSelect: () => void;
  onSimplify?: (english?: ViewCommunicationItem, spanish?: ViewCommunicationItem) => Promise<void>;
  onTranslate: (id: string) => Promise<void>;
  canSpanish: boolean;
}) {
  const [isSimplifying, setIsSimplifying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [englishText, setEnglishText] = useState(english?.communication ?? '');
  const [spanishText, setSpanishText] = useState(spanish?.communication ?? '');

  const isTranslateDisabled = useMemo(() => spanish?.isGenerated === false, [spanish]);

  const saving = isSaving || isSavingOutside;

  useEffect(() => {
    setEnglishText(english?.communication ?? '');
    setSpanishText(spanish?.communication ?? '');
  }, [english, spanish]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSelect();
    }
  };

  const handleBlur = async (
    e: React.FocusEvent,
    lang: CommunicationLanguage,
    val: string,
    commId?: string
  ) => {
    if ((!commId && val.trim() === '') || isSimplifying) return; // don't create empty comm items
    if (lang === CommunicationLanguage.EN && val === english?.communication) return; // don't save if no change
    if (lang === CommunicationLanguage.ES && val === spanish?.communication) return; // don't save if not change
    setIsSaving(true);
    const result = await onSaveCommunicationItem(lang, val, commId);
    if (result && lang !== CommunicationLanguage.ES && !isTranslateDisabled) {
      await onTranslate(result.id!);
    }
    setIsSaving(false);
  };

  const handleSimplify = async (e: React.MouseEvent) => {
    setIsSimplifying(true);
    await onSimplify?.({ ...english, communication: englishText }, spanish);
    setIsSimplifying(false);
  };

  useEffect(() => {
    setIsSaving(isSimplifying);
  }, [isSimplifying]);

  const getSimplifyButton = () => {
    if (isSimplified) {
      return (
        <span className="px-1">
          <Check className={'text-success'} />
          &nbsp;Simplified
        </span>
      );
    } else {
      return (
        <Button
          size="sm"
          variant={'outline-primary'}
          className={`${isSimplifying ? 'suggesting' : ''}`}
          onMouseDown={handleSimplify} // mouseDown runs before onBlur
          disabled={readonly || isSimplifying || !isSelected || saving}
        >
          <Sparkle sparkling={isSimplifying} />
          &nbsp;
          {isSimplifying ? 'Simplifying...' : 'Simplify'}
        </Button>
      );
    }
  };

  return (
    <Card className={`int-li border-0`} key={interrogatory.id} ref={liRef}>
      <Card.Header className="border-0 d-flex justify-content-between align-items-center">
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          onKeyDownCapture={handleKeyPress}
          readOnly={readonly}
          id={`question-${interrogatory.number}`}
          name={`question-${interrogatory.number}`}
          label={`${questionType} ${interrogatory.number}`}
          className="py-1"
        />
        {isSelected && onSimplify && !readonly && getSimplifyButton()}
      </Card.Header>
      <Card.Body className={`d-flex flex-column gap-3 ${!isSelected ? 'disabled' : ''}`}>
        <span dangerouslySetInnerHTML={{ __html: sanitizeHtmlString(`${interrogatory.text}`) }} />
        <Stack className="flex-column flex-md-row align-items-center" gap={2}>
          <CommunicationItem
            disabled={!isSelected || saving}
            readonly={readonly}
            onBlur={(e) => handleBlur(e, CommunicationLanguage.EN, englishText ?? '', english?.id)}
            formId={`message-${interrogatory.number}`}
            label="Message to Client (Optional)"
            text={englishText}
            setText={setEnglishText}
          />

          {canSpanish && language !== ContactLanguages.EnglishUs && (
            <>
              <TranslationArrow
                disabled={spanish?.isGenerated === false}
                className="d-md-none"
                direction="down"
              />
              <TranslationArrow
                disabled={spanish?.isGenerated === false}
                className="d-none d-md-block"
                direction="right"
              />
              <CommunicationItem
                disabled={!isSelected || saving}
                readonly={readonly}
                onBlur={(e) => handleBlur(e, CommunicationLanguage.ES, spanishText ?? '', spanish?.id)}
                label={
                  <span>
                    Spanish Translation
                    <Help
                      id={`help-text-spanish-${interrogatory.id}`}
                      text="Changing the Spanish translation will disable the translation feature for this item."
                    />
                  </span>
                }
                formId={`translated-message-${interrogatory.number}`}
                text={spanishText}
                setText={setSpanishText}
              />
            </>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
}

function CommunicationItem({
  disabled,
  readonly,
  onBlur,
  formId,
  label,
  text,
  setText,
}: {
  disabled: boolean;
  readonly: boolean;
  formId: string;
  label: React.ReactNode;
  text?: string;
  setText: React.Dispatch<React.SetStateAction<string>>;
  onBlur: (e: React.FocusEvent) => void;
}) {
  return (
    <Stack>
      <label htmlFor={formId} className="fw-bold">
        {label}
      </label>
      <Form.Control
        as="textarea"
        placeholder="Leave a comment here"
        style={{ height: '200px' }}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={onBlur}
        readOnly={readonly}
        id={formId}
        name={formId}
        disabled={disabled}
      />
    </Stack>
  );
}

function TranslationArrow({
  direction,
  disabled,
  className,
}: {
  direction: 'down' | 'right';
  disabled: boolean;
  className?: string;
}) {
  const rotation = direction === 'down' ? 'rotate(90deg)' : 'none';
  const ArrowComponent = disabled ? ArrowOff : Arrow;
  return <ArrowComponent className={className} style={{ transform: rotation }} />;
}
